'use client';

import { c as _c } from "react/compiler-runtime";
import type { EditorView } from '@codemirror/view';
import { ChevronUpIcon } from '@heroicons/react/16/solid';
import type React from 'react';
import { useEffect, useState } from 'react';
import { H2, H3, H4 } from '@/frontend/design-system/typography/Heading';
import { Label3 } from '@/frontend/design-system/typography/Label';
import { P2 } from '@/frontend/design-system/typography/Paragraph';
import { cn } from '@/frontend/utils';
import { useDropdown } from '../useDropdown';
import { insertHeading1, insertHeading2, insertHeading3, insertNormalText } from './textStyleActions';
type Option = {
  label: string;
  component: React.ElementType;
  action: () => void;
};
export const TextDropdown: React.FC<{
  editorView: EditorView | null;
  isEditable?: boolean;
}> = t0 => {
  const $ = _c(45);
  const {
    editorView,
    isEditable: t1
  } = t0;
  const isEditable = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== editorView) {
    t2 = {
      label: "Nag\u0142\xF3wek 1",
      component: H2,
      action: () => insertHeading1(editorView)
    };
    $[0] = editorView;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== editorView) {
    t3 = {
      label: "Nag\u0142\xF3wek 2",
      component: H3,
      action: () => insertHeading2(editorView)
    };
    $[2] = editorView;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== editorView) {
    t4 = {
      label: "Nag\u0142\xF3wek 3",
      component: H4,
      action: () => insertHeading3(editorView)
    };
    $[4] = editorView;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== editorView) {
    t5 = {
      label: "Tekst",
      component: P2,
      action: () => insertNormalText(editorView)
    };
    $[6] = editorView;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = [t2, t3, t4, t5];
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const options = t6;
  const [selected, setSelected] = useState(options[3]);
  const {
    open,
    setOpen,
    ref
  } = useDropdown();
  let t7;
  if ($[13] !== editorView || $[14] !== options[0] || $[15] !== options[1] || $[16] !== options[2] || $[17] !== options[3]) {
    t7 = () => {
      if (!editorView) {
        return options[3];
      }
      const {
        selection
      } = editorView.state;
      const line = editorView.state.doc.lineAt(selection.main.from);
      const lineText = line.text;
      let level = 0;
      while (level < lineText.length && lineText[level] === "#") {
        level++;
      }
      if (level > 0 && lineText[level] === " ") {
        switch (level) {
          case 1:
            {
              return options[0];
            }
          case 2:
            {
              return options[1];
            }
          case 3:
            {
              return options[2];
            }
        }
      }
      return options[3];
    };
    $[13] = editorView;
    $[14] = options[0];
    $[15] = options[1];
    $[16] = options[2];
    $[17] = options[3];
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const detectCurrentStyle = t7;
  let t8;
  if ($[19] !== detectCurrentStyle || $[20] !== editorView) {
    t8 = () => {
      if (!editorView) {
        return;
      }
      const update = () => setSelected(detectCurrentStyle());
      editorView.dom.addEventListener("click", update);
      editorView.dom.addEventListener("keyup", update);
      return () => {
        editorView.dom.removeEventListener("click", update);
        editorView.dom.removeEventListener("keyup", update);
      };
    };
    $[19] = detectCurrentStyle;
    $[20] = editorView;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useEffect(t8);
  let t9;
  if ($[22] !== setOpen) {
    t9 = option => {
      setSelected(option);
      option.action();
      setOpen(false);
    };
    $[22] = setOpen;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleSelect = t9;
  let t10;
  if ($[24] !== setOpen) {
    t10 = () => setOpen(_temp);
    $[24] = setOpen;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== selected.label) {
    t11 = <Label3 className="text-nowrap">{selected.label}</Label3>;
    $[26] = selected.label;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = open && "rotate-180";
  let t13;
  if ($[28] !== t12) {
    t13 = cn("h-4 w-4 transition-transform", t12);
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <ChevronUpIcon className={t13} />;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t14) {
    t15 = <button type="button" onClick={t10} className="flex cursor-default items-center gap-1 rounded-md p-1.5 transition-colors hover:bg-backgroundHoverOverlay">{t11}{t14}</button>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== handleSelect || $[37] !== isEditable || $[38] !== open || $[39] !== options) {
    t16 = open && <div className="absolute bottom-full left-0 mb-3 w-50 -translate-x-2 rounded-md border border-borderPrimary bg-backgroundTertiary p-3">{options.map(opt => {
        const Component = opt.component;
        return <button type="button" key={opt.label} onClick={() => handleSelect(opt)} disabled={!isEditable} className={cn("w-full cursor-default text-nowrap rounded-sm p-2 text-left hover:bg-backgroundHoverOverlay", !isEditable && "pointer-events-none opacity-60")}><Component>{opt.label}</Component></button>;
      })}</div>;
    $[36] = handleSelect;
    $[37] = isEditable;
    $[38] = open;
    $[39] = options;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== ref || $[42] !== t15 || $[43] !== t16) {
    t17 = <div className="relative" ref={ref}>{t15}{t16}</div>;
    $[41] = ref;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
};
function _temp(prev) {
  return !prev;
}